import React, { useState, useEffect } from "react";
import API from "../api/axios";
import toast from "react-hot-toast";

const Integrations = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    whatsapp_phone_number_id: "",
    whatsapp_api_key: "",

    auto_welcome_enabled: false,
    welcome_message_type: "text",
    welcome_message_template: "",

    welcome_list_header: "",
    welcome_list_body: "",
    welcome_list_button_text: "",
    welcome_list_section_title: "",
    welcome_list_items: [{ id: "", title: "", description: "" }],
  });

  useEffect(() => {
    fetchIntegration();
  }, []);

  const fetchIntegration = async () => {
    try {
      const res = await API.get("/integrations");
      const data = res?.data?.data || {};

      setFormData({
        whatsapp_phone_number_id: data.whatsapp_phone_number_id || "",
        whatsapp_api_key: data.whatsapp_api_key || "",

        auto_welcome_enabled: !!data.auto_welcome_enabled,
        welcome_message_type: data.welcome_message_type || "text",
        welcome_message_template: data.welcome_message_template || "",

        welcome_list_header: data.welcome_list_header || "",
        welcome_list_body: data.welcome_list_body || "",
        welcome_list_button_text: data.welcome_list_button_text || "",
        welcome_list_section_title: data.welcome_list_section_title || "",
        welcome_list_items:
          Array.isArray(data.welcome_list_items) &&
          data.welcome_list_items.length > 0
            ? data.welcome_list_items
            : [{ id: "", title: "", description: "" }],
      });
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleListItemChange = (index, field, value) => {
    const updatedItems = [...formData.welcome_list_items];
    updatedItems[index][field] = value;

    setFormData((prev) => ({
      ...prev,
      welcome_list_items: updatedItems,
    }));
  };

  const addListItem = () => {
    setFormData((prev) => ({
      ...prev,
      welcome_list_items: [
        ...prev.welcome_list_items,
        { id: "", title: "", description: "" },
      ],
    }));
  };

  const removeListItem = (index) => {
    const updatedItems = formData.welcome_list_items.filter(
      (_, i) => i !== index
    );

    setFormData((prev) => ({
      ...prev,
      welcome_list_items:
        updatedItems.length > 0
          ? updatedItems
          : [{ id: "", title: "", description: "" }],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    if (!formData.whatsapp_phone_number_id || !formData.whatsapp_api_key) {
      toast.error("Phone Number ID and API Key are required");
      return;
    }

    if (formData.auto_welcome_enabled) {
      if (formData.welcome_message_type === "text") {
        if (!formData.welcome_message_template.trim()) {
          toast.error("Please enter welcome text message");
          return;
        }
      }

      if (formData.welcome_message_type === "list") {
        if (
          !formData.welcome_list_header.trim() ||
          !formData.welcome_list_body.trim() ||
          !formData.welcome_list_button_text.trim() ||
          !formData.welcome_list_section_title.trim()
        ) {
          toast.error("Please fill all list message fields");
          return;
        }

        const validItems = formData.welcome_list_items.filter(
          (item) => item.title.trim() !== ""
        );

        if (validItems.length === 0) {
          toast.error("Please add at least one valid list item");
          return;
        }
      }
    }

    setSaving(true);

    try {
      const payload = {
        ...formData,
        welcome_list_items: formData.welcome_list_items.filter(
          (item) => item.title.trim() !== ""
        ),
      };

      const res = await API.put("/integrations", payload);
      toast.success(res?.data?.message || "Settings saved successfully");
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to save settings"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          WhatsApp Integration
        </h1>
        <p className="text-gray-500 mt-1">
          Connect your WhatsApp and configure automatic welcome messages
        </p>
      </div>

      {/* Main Settings */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-xl">
            💬
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              AOC Portal Settings
            </h2>
            <p className="text-sm text-gray-500">
              Enter your WhatsApp API credentials and welcome automation settings
            </p>
          </div>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {/* Phone Number ID */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone Number ID
            </label>
            <input
              type="text"
              name="whatsapp_phone_number_id"
              value={formData.whatsapp_phone_number_id}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="e.g., 919876543210"
            />
            <p className="text-xs text-gray-400 mt-1">
              Your WhatsApp business number on AOC Portal (without +)
            </p>
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key
            </label>
            <input
              type="password"
              name="whatsapp_api_key"
              value={formData.whatsapp_api_key}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="Enter your AOC Portal API key"
            />
            <p className="text-xs text-gray-400 mt-1">
              Found in AOC Portal → Settings → API Keys
            </p>
          </div>

          {/* Auto Welcome Toggle */}
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              name="auto_welcome_enabled"
              checked={formData.auto_welcome_enabled}
              onChange={handleInputChange}
              className="h-4 w-4"
            />
            <label className="text-sm font-medium text-gray-700">
              Enable Auto Welcome Message
            </label>
          </div>

          {/* Show only when enabled */}
          {formData.auto_welcome_enabled && (
            <div className="space-y-6 border border-gray-200 rounded-xl p-5 bg-gray-50">
              {/* Welcome Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Welcome Message Type
                </label>
                <select
                  name="welcome_message_type"
                  value={formData.welcome_message_type}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                >
                  <option value="text">Text Message</option>
                  <option value="list">List Message</option>
                </select>
              </div>

              {/* TEXT MESSAGE MODE */}
              {formData.welcome_message_type === "text" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Welcome Text Message
                  </label>
                  <textarea
                    name="welcome_message_template"
                    value={formData.welcome_message_template}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder={`Hi {{name}}, welcome to {{company_name}} 🎉\n\nThank you for contacting us.`}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Variables available:{" "}
                    <code>{"{{name}}"}</code>,{" "}
                    <code>{"{{company_name}}"}</code>,{" "}
                    <code>{"{{phone}}"}</code>
                  </p>
                </div>
              )}

              {/* LIST MESSAGE MODE */}
              {formData.welcome_message_type === "list" && (
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      List Header
                    </label>
                    <input
                      type="text"
                      name="welcome_list_header"
                      value={formData.welcome_list_header}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Hello {{name}} ✨"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      List Body
                    </label>
                    <textarea
                      name="welcome_list_body"
                      value={formData.welcome_list_body}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Welcome to {{company_name}}. Please choose one option below."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Button Text
                    </label>
                    <input
                      type="text"
                      name="welcome_list_button_text"
                      value={formData.welcome_list_button_text}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Select Service"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Section Title
                    </label>
                    <input
                      type="text"
                      name="welcome_list_section_title"
                      value={formData.welcome_list_section_title}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Our Services"
                    />
                  </div>

                  {/* Dynamic List Items */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      List Items
                    </label>

                    <div className="space-y-4">
                      {formData.welcome_list_items.map((item, index) => (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg p-4 bg-white space-y-3"
                        >
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Item ID
                            </label>
                            <input
                              type="text"
                              value={item.id}
                              onChange={(e) =>
                                handleListItemChange(index, "id", e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              placeholder="service_astrology"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Item Title
                            </label>
                            <input
                              type="text"
                              value={item.title}
                              onChange={(e) =>
                                handleListItemChange(index, "title", e.target.value)
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              placeholder="Astrology"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Item Description (Optional)
                            </label>
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) =>
                                handleListItemChange(
                                  index,
                                  "description",
                                  e.target.value
                                )
                              }
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              placeholder="Know your future"
                            />
                          </div>

                          <button
                            type="button"
                            onClick={() => removeListItem(index)}
                            className="text-red-600 text-sm font-medium"
                          >
                            Remove Item
                          </button>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={addListItem}
                      className="mt-4 bg-gray-900 hover:bg-black text-white font-medium py-2 px-4 rounded-lg transition-colors"
                    >
                      + Add List Item
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Save */}
          <button
            type="submit"
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition-colors disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </form>
      </div>

      {/* How it works */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          ⚡ How It Works
        </h3>
        <div className="space-y-3 text-sm text-gray-600">
          <div className="flex items-start gap-3">
            <span className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">
              1
            </span>
            <span>Save your Phone Number ID and API Key above</span>
          </div>

          <div className="flex items-start gap-3">
            <span className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">
              2
            </span>
            <span>
              Enable auto welcome and choose whether to send a text or list message
            </span>
          </div>

          <div className="flex items-start gap-3">
            <span className="bg-blue-100 text-blue-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">
              3
            </span>
            <span>
              When someone messages your WhatsApp number for the first time, a lead is created automatically and welcome message is sent instantly
            </span>
          </div>

          <div className="flex items-start gap-3">
            <span className="bg-green-100 text-green-700 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold flex-shrink-0">
              ✓
            </span>
            <span>
              New contact = New lead + auto welcome. Existing contact = message added as remark only.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Integrations;